/* RESPONSIBLE TEAM: team-standalone */

import StandardizedPlatform from './standardized-platform';
import { IntegrationState, IntegrationType, StandalonePlatform } from '../constants';
import { ConnectionType } from 'embercom/objects/environments/connection-types';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { inject as service } from '@ember/service';
import type ApplicationInstance from '@ember/application/instance';
import { GenericConversationExtractionRowData } from './component-data/conversation-extraction-row-data';
import FreshdeskConversationIngestion from './component-data/freshdesk/conversation-ingestion';
import GenericConversationExcerpt from './component-data/generic/conversation-excerpt';
import GenericOnboardingGuideStep from './component-data/generic/onboarding-guide-step';
import StandardOnboardingGuideStep from './component-data/standard-onboarding-guide-step';
import GenericRequestAccessData from './component-data/generic/request-access';
import FreshdeskChannelTargetingBodyData from './component-data/freshdesk/channel-targeting-body-data';
import { GenericAiContentLibraryData } from './component-data/ai-content-library';
import { GenericKnowledgeContentData } from './component-data/knowlege-content-data';
import { GenericComposerOverflowMenuTooltipData } from './component-data/overflow-menu-tooltip-data';
import { GenericExampleCodeSnippetData } from './component-data/example-code-snippet-data';
import type Platform from './platform';

export default class FreshdeskPlatform extends StandardizedPlatform {
  @service declare finStandaloneService: FinStandaloneService;

  constructor(owner: ApplicationInstance) {
    super(owner);
  }

  protected get connectionTypes(): ConnectionType[] {
    return [ConnectionType.FreshdeskApi, ConnectionType.FreshchatApi];
  }

  override get canUseReactStepBasedIA(): boolean {
    return this.finStandaloneService.app.canUseReactFreshdeskDeployPages;
  }

  get componentData(): Platform['componentData'] {
    return {
      'conversation-extraction': GenericConversationExtractionRowData,
      'conversation-excerpt': new GenericConversationExcerpt(this),
      'knowledge-hub-modal': null,
      'onboarding-guide-step': this.canUseReactStepBasedIA
        ? new StandardOnboardingGuideStep(this)
        : new GenericOnboardingGuideStep(this),
      'request-access': new GenericRequestAccessData(this),
      'channel-targeting-body': new FreshdeskChannelTargetingBodyData(),
      'ai-content-library': GenericAiContentLibraryData,
      'knowledge-content': GenericKnowledgeContentData,
      'composer-overflow-menu-tooltip': GenericComposerOverflowMenuTooltipData,
      'example-code-snippet': GenericExampleCodeSnippetData,
      'conversation-ingestion': new FreshdeskConversationIngestion(this),
    } as const;
  }

  async loadConfigData() {
    // TODO
  }

  async loadRequiredData() {
    return super.loadRequiredData();
  }

  get supportsErrorLogs() {
    return true;
  }

  get showOauthBanner() {
    return false;
  }

  async loadDataFields() {
    return super.loadDataFields();
  }

  get isConnected() {
    let ticketsIntegration = this.finStandaloneService.standaloneIntegrations.find(
      (int) => int.integrationType === IntegrationType.FreshdeskTickets,
    );
    return ticketsIntegration?.integrationId != null;
  }

  get hasInstalledCopilot() {
    return false;
  }

  get supportsHistoricalSyncing() {
    return true;
  }

  get historicalSyncingEnabled() {
    return (this.isConnected && this.finStandaloneService.isConversationSyncingEnabled) || false;
  }

  get finIsLive() {
    return false;
  }

  get finIsPaused() {
    return false;
  }

  get showAnalyzeSectionConnectCta() {
    return false;
  }

  get connectionState() {
    return IntegrationState.NotConfigured;
  }

  get icon() {
    return 'freshdesk' as const;
  }

  get platformKey() {
    return StandalonePlatform.Freshdesk;
  }

  get supportsFinPlayground() {
    return false;
  }

  get hasMessengerSettings() {
    return this.appService.app.canUseReactFreshdeskDeployPages;
  }

  get supportsConversationSyncing() {
    return true;
  }

  findMappedConversationField(_id: string | undefined) {
    return undefined;
  }

  isMappedUserAttribute(attributeIdentifier: string) {
    return super.isMappedUserAttribute(attributeIdentifier);
  }

  isMappedCompanyAttribute(attributeIdentifier: string) {
    return super.isMappedCompanyAttribute(attributeIdentifier);
  }

  isMappedConversationAttribute(attributeIdentifier: string) {
    return super.isMappedConversationAttribute(attributeIdentifier);
  }

  get ticketFields() {
    return super.ticketFields;
  }

  get userFields() {
    return super.userFields;
  }

  get companyFields() {
    return super.companyFields;
  }
}
